// VOY-485 (rebuild) — Plan + Wallet (Public profile tab).
//
// Mirrors the live `PlanWalletCard` adaptive CTA logic:
//   - free / no sub        → "Upgrade" → /subscription
//   - active + renewing    → "Manage"  → /subscription
//   - cancelled in-period  → "Reactivate" → POST /api/subscriptions/:id/reactivate
//   - expired (no sub_id)  → fall back to fresh checkout via createCheckoutSession
//
// Coins + Tokens shown from `profiles.voy_coins` / `voy_tokens` — these
// are real fields the user accumulates (e.g. profile-completion reward
// adds 50 Coins). They are NOT gaming-layer fields; do not placeholder
// them. The gaming layer is separate (Badges/Gaming sections).

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { getTierFromSubscription, createCheckoutSession, reactivateSubscription } from '@/lib/stripe'
import type { Profile } from '@/types/profile'
import type { UserSubscription } from '@/types/subscription'

export interface PlanWalletSectionProps {
  profile: Profile
  subscription: UserSubscription | null
  onRefresh?: () => void
}

type Action = 'upgrade' | 'manage' | 'reactivate'

function resolveAction(tier: string, subscription: UserSubscription | null): Action {
  if (tier === 'free' || !subscription) return 'upgrade'
  const cancelled =
    subscription.cancel_at_period_end === true ||
    subscription.status === 'canceled' ||
    subscription.status === 'unpaid' ||
    subscription.status === 'incomplete_expired'
  if (cancelled) return 'reactivate'
  return 'manage'
}

export function PlanWalletSection({ profile, subscription, onRefresh }: PlanWalletSectionProps) {
  const [isReactivating, setIsReactivating] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const tier = getTierFromSubscription(subscription)
  const planLabel = tier === 'free' ? 'Free' : tier.charAt(0).toUpperCase() + tier.slice(1)
  const action = resolveAction(tier, subscription)

  async function handleReactivate() {
    if (!subscription?.subscription_id) {
      if (subscription?.price_id) {
        try {
          const url = await createCheckoutSession(subscription.price_id, 'subscription')
          window.location.href = url
        } catch (err) {
          console.error('[PlanWalletSection] checkout failed:', err)
          setMessage({ type: 'error', text: 'Failed to start renewal. Please try again.' })
        }
      }
      return
    }
    setIsReactivating(true)
    setMessage(null)
    try {
      // VOY-608: also undoes a pending downgrade (cancel_at_period_end on an active plan).
      await reactivateSubscription(subscription.subscription_id)
      setMessage({ type: 'success', text: 'Subscription reactivated.' })
      setTimeout(() => {
        onRefresh?.()
        setMessage(null)
        setIsReactivating(false)
      }, 1200)
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'Failed to reactivate subscription.'
      // Success keeps the button disabled until the refresh above fires (no double submit).
      setIsReactivating(false)
      console.error('[PlanWalletSection] reactivate failed:', err)
      setMessage({ type: 'error', text: errMsg })
    }
  }

  const ctaClass = 'btn-primary'

  return (
    <section className="card" aria-labelledby="plan-h">
      <div className="card-h">
        <h3 id="plan-h">Plan &amp; wallet</h3>
        <span className="meta">{planLabel.toUpperCase()}</span>
      </div>

      <div className="row between" style={{ alignItems: 'center' }}>
        <div>
          <div className="lbl">CURRENT PLAN</div>
          <div className="num" style={{ fontSize: 18, marginTop: 4 }}>{planLabel}</div>
        </div>
        {action === 'reactivate' ? (
          <button type="button" onClick={handleReactivate} disabled={isReactivating} aria-label={isReactivating ? 'Reactivating' : undefined} className={ctaClass} style={{ padding: '7px 14px', fontSize: 12 }}>
            {isReactivating ? <Loader2 className="kw-spin" aria-hidden="true" /> : 'Reactivate'}
          </button>
        ) : action === 'manage' ? (
          <Link to="/subscription" className={ctaClass} style={{ padding: '7px 14px', fontSize: 12 }}>Manage</Link>
        ) : (
          <Link to="/subscription" className={ctaClass} style={{ padding: '7px 14px', fontSize: 12 }}>Upgrade</Link>
        )}
      </div>

      {message && (
        <p style={{ fontSize: 12, marginTop: 8, color: message.type === 'success' ? 'var(--v6-pos)' : 'var(--v6-neg)' }} role="status">
          {message.text}
        </p>
      )}

      <div className="divider" />
      <div className="grid-2" style={{ gap: 14 }}>
        <div>
          <div className="lbl">VOY COINS</div>
          <div className="num" style={{ fontSize: 22, marginTop: 4, color: 'var(--v6-accent)' }}>
            {(profile.voy_coins ?? 0).toLocaleString()}
          </div>
        </div>
        <div>
          <div className="lbl">VOY TOKENS</div>
          <div className="num" style={{ fontSize: 22, marginTop: 4, color: 'var(--v6-pos)' }}>
            {(profile.voy_tokens ?? 0).toLocaleString()}
          </div>
        </div>
      </div>
    </section>
  )
}
